import type { ApplicationApi } from "./applicationApi";
import type { ConfigurableLoanEventType } from "@loan-management/contracts";

export interface DashboardResponse {
  maturities: Array<{
    id: string;
    loan_number: string;
    maturity_date: string;
    name: string;
  }>;
  summary: {
    active_loans: number;
    balloon_due: string;
    current_principal: string;
    debt_service_due: string;
    interest_due: string;
    unreconciled: string;
    weighted_rate: string;
  };
}

export interface LenderGroupRecord {
  code: string;
  concentration_threshold: string | null;
  group_type: string;
  id: string;
  name: string;
  notes: string | null;
  relationship_owner: string | null;
  status: "active" | "inactive";
}

export interface LenderRecord {
  code: string;
  contact_email: string | null;
  contact_phone: string | null;
  id: string;
  lender_group_id: string | null;
  lender_group_name: string | null;
  loan_count: number;
  name: string;
  notes: string | null;
  primary_contact: string | null;
  relationship_manager: string | null;
  sage_vendor_id: string | null;
  sage_vendor_name: string | null;
  status: "active" | "inactive";
}

export interface SageVendorOption {
  display_name: string;
  id: string;
  sage_record_id: string | null;
  sage_record_no: string;
}

export interface LoanListRecord {
  borrowing_entity_name: string;
  currency_code: string;
  current_principal: string | null;
  current_rate: string | null;
  id: string;
  lender_name: string | null;
  loan_number: string;
  loan_type: string;
  maturity_date: string | null;
  name: string;
  next_payment_amount: string | null;
  next_payment_date: string | null;
  original_principal: string | null;
  schedule_status: string | null;
  status: string;
  term_status: string | null;
}

export interface LoanOptionsResponse {
  entities: Array<{ display_name: string; id: string; sage_record_no: string }>;
  lenders: Array<{
    code: string;
    id: string;
    lender_group_id: string | null;
    name: string;
  }>;
}

export interface LoanDetailResponse {
  accountingMappings: Array<{
    mapping_key: string;
    sage_master_record_id: string;
  }>;
  accountingOptions: Array<{
    dimension_key: string | null;
    display_name: string;
    id: string;
    record_type: string;
    sage_record_no: string;
    status: "active" | "inactive";
  }>;
  assets: Array<{
    asset_identifier: string | null;
    asset_type:
      | "real_estate"
      | "vehicle"
      | "equipment"
      | "inventory"
      | "receivable"
      | "securities"
      | "guarantee"
      | "other";
    description: string;
    estimated_value: string | null;
    id: string;
    make: string | null;
    model: string | null;
    model_year: number | null;
    sage_fixed_asset_id: string | null;
    valuation_date: string | null;
    vin: string | null;
  }>;
  events: Array<Record<string, unknown>>;
  fees: Array<Record<string, unknown>>;
  lenders: Array<{
    allocation_percent: string;
    code: string;
    id: string;
    is_primary: boolean;
    name: string;
  }>;
  loan: Record<string, unknown>;
  notes: Array<{ created_at: string; id: string; note: string }>;
  payments: Array<{
    additional_principal_amount: string;
    amount: string;
    id: string;
    interest_amount: string;
    payment_date: string;
    period_number: number;
    principal_amount: string;
    sage_reference: string | null;
    schedule_period_id: string;
    scheduled_cash_payment: string;
    status: "confirmed" | "voided" | "posted";
  }>;
  periods: Array<
    Record<string, unknown> & {
      gl_batches: Array<{ recordNo: string; recordUrl: string | null }>;
      posting_state: "scheduled" | "partially_posted" | "posted";
    }
  >;
  rates: Array<Record<string, unknown>>;
  scheduleValidation: Array<{
    code: string;
    message: string;
    period_number: number | null;
    severity: "error" | "warning";
  }>;
  schedules: Array<Record<string, unknown>>;
  terms: Array<
    Record<string, unknown> & {
      enabled_event_types?: ConfigurableLoanEventType[];
    }
  >;
}

export function getLoanView<T>(
  api: ApplicationApi,
  view: string,
  parameters: Record<string, string> = {},
): Promise<T> {
  const query = new URLSearchParams({ view, ...parameters });
  return api.get<T>(`loan-operations?${query.toString()}`);
}

export function performLoanAction<T>(
  api: ApplicationApi,
  body: Record<string, unknown>,
): Promise<T> {
  return api.post<T, Record<string, unknown>>("loan-operations", body);
}
